import { imageAssets } from './imageAssets';
import type { BrandsValuePageData } from './types';
const assets = imageAssets;
const heroImages = assets.heroImages;
const heroLogos = assets.heroLogos;
let richTextUid = 0;
const nextRichTextUid = () => {
  const uid = `brands-value-faq-${richTextUid}`;
  richTextUid += 1;
  return uid;
};
const textNode = (text: string) => ({
  text
});
const textBreakNode = (text: string) => ({
  text,
  break: true
});
const boldTextNode = (text: string, breakLine = false) => ({
  text,
  bold: true,
  ...(breakLine ? {
    break: true
  } : {})
});
const breakTextNode = (text = '\n') => ({
  text,
  break: true
});
const linkNode = ({
  href,
  text
}: {
  href: string;
  text: string;
}) => ({
  type: 'a' as const,
  attrs: {
    url: href,
    target: '_self' as const
  },
  uid: nextRichTextUid(),
  children: [textNode(text)]
});
const paragraph = (text: string, children: (ReturnType<typeof textNode> | ReturnType<typeof textBreakNode> | ReturnType<typeof boldTextNode> | ReturnType<typeof breakTextNode> | ReturnType<typeof linkNode>)[] = [textNode(text)]) => ({
  type: 'p' as const,
  attrs: {},
  uid: nextRichTextUid(),
  children
});
const richText = (children: ReturnType<typeof paragraph>[]): BrandsValuePageData['faqs']['items'][number]['body'] => ({
  type: 'doc',
  attrs: {},
  uid: nextRichTextUid(),
  children
});
const titleRichText = (text: string): NonNullable<BrandsValuePageData['faqs']['moduleTitle']>['title'] => ({
  type: 'doc',
  attrs: {},
  uid: nextRichTextUid(),
  children: [{
    type: 'h2',
    attrs: {},
    uid: nextRichTextUid(),
    children: [textNode(text)]
  }]
});
const emptyRichText = richText([]);
export const brandsValuePageData: BrandsValuePageData = {
  hero: {
    title: 'Personal styling that fits you',
    description: "Connect with a real Stylist who knows your budget, fit and style—and they'll shop for you.",
    eyebrow: 'No subscription required · Try before you buy',
    ctaLabel: 'Claim $30 off + free styling',
    ctaHref: '/signup',
    columns: [{
      id: 'hero-col-1',
      variant: 'narrow-stack',
      tiles: [{
        kind: 'logo',
        ...heroLogos.madewell,
        alt: 'Madewell logo',
        brand: 'madewell'
      }, {
        kind: 'image',
        ...heroImages[0],
        alt: 'Model in a striped top and shorts on a porch'
      }]
    }, {
      id: 'hero-col-2',
      variant: 'tall',
      tiles: [{
        kind: 'image',
        ...heroImages[1],
        alt: 'Model in a neutral sweater'
      }]
    }, {
      id: 'hero-col-3',
      variant: 'narrow-stack',
      tiles: [{
        kind: 'logo',
        ...heroLogos.birkenstock,
        alt: 'Birkenstock logo',
        brand: 'birkenstock'
      }, {
        kind: 'image',
        ...heroImages[2],
        alt: 'Children wearing casual outfits outdoors'
      }]
    }, {
      id: 'hero-col-4',
      variant: 'tall',
      tiles: [{
        kind: 'image',
        ...heroImages[3],
        alt: 'Model in a floral dress'
      }]
    }, {
      id: 'hero-col-5',
      variant: 'narrow-stack',
      tiles: [{
        kind: 'logo',
        ...heroLogos.freePeople,
        alt: 'Free People logo',
        brand: 'free-people'
      }, {
        kind: 'image',
        ...heroImages[4],
        alt: 'Model in a pink suit'
      }]
    }, {
      id: 'hero-col-6',
      variant: 'tall',
      tiles: [{
        kind: 'image',
        ...heroImages[5],
        alt: 'Model seated on a porch in a light outfit'
      }]
    }, {
      id: 'hero-col-7',
      variant: 'narrow-stack',
      tiles: [{
        kind: 'logo',
        ...heroLogos.abercrombie,
        alt: 'Abercrombie kids logo',
        brand: 'abercrombie'
      }, {
        kind: 'image',
        ...heroImages[6],
        alt: 'Child in a pink outfit in a bedroom'
      }]
    }, {
      id: 'hero-col-8',
      variant: 'tall',
      tiles: [{
        kind: 'image',
        ...heroImages[7],
        alt: 'Model in a blue blazer standing on a porch'
      }]
    }, {
      id: 'hero-col-9',
      variant: 'narrow-stack',
      tiles: [{
        kind: 'logo',
        ...heroLogos.vuori,
        alt: 'Vuori logo',
        brand: 'vuori'
      }, {
        kind: 'image',
        ...heroImages[8],
        alt: 'Tennis player on a court'
      }]
    }, {
      id: 'hero-col-10',
      variant: 'tall',
      tiles: [{
        kind: 'image',
        ...heroImages[9],
        alt: 'Model wearing a maroon outfit'
      }]
    }, {
      id: 'hero-col-11',
      variant: 'wide-stack',
      tiles: [{
        kind: 'logo',
        ...heroLogos.adidas,
        alt: 'Adidas logo',
        brand: 'adidas'
      }, {
        kind: 'image',
        ...heroImages[10],
        alt: 'Model seated in a cap and tan jacket'
      }]
    }, {
      id: 'hero-col-12',
      variant: 'tall',
      tiles: [{
        kind: 'image',
        ...heroImages[11],
        alt: 'Model in a brown top and white pants on a porch'
      }]
    }, {
      id: 'hero-col-13',
      variant: 'narrow-stack',
      tiles: [{
        kind: 'logo',
        ...heroLogos.varley,
        alt: 'Varley logo',
        brand: 'varley'
      }, {
        kind: 'image',
        ...heroImages[12],
        alt: 'Model in a pink polo on a tennis court'
      }]
    }, {
      id: 'hero-col-14',
      variant: 'tall',
      tiles: [{
        kind: 'image',
        ...heroImages[13],
        alt: 'Model carrying a beach bag'
      }]
    }, {
      id: 'hero-col-15',
      variant: 'narrow-stack',
      tiles: [{
        kind: 'logo',
        ...heroLogos.faherty,
        alt: 'Faherty logo',
        brand: 'faherty'
      }, {
        kind: 'image',
        ...heroImages[14],
        alt: 'Model in a striped dress'
      }]
    }, {
      id: 'hero-col-16',
      variant: 'tall',
      tiles: [{
        kind: 'image',
        ...heroImages[15],
        alt: 'Smiling couple in casual outfits'
      }]
    }]
  },
  pricing: {
    eyebrow: '',
    title: 'Only pay for what you keep',
    supportingText: 'Always free shipping & returns',
    cards: [{
      id: 'price-1',
      eyebrow: 'items starting at $29',
      title: 'Set your own budget',
      description: 'Your Stylist will stay within it.',
      accent: 'blue',
      bordered: true
    }, {
      id: 'price-2',
      eyebrow: 'RISK FREE',
      title: 'First styling is on us',
      description: "We'll waive your first $20 styling fee. In the future, it's always credited toward anything you keep.",
      accent: 'citrus',
      bordered: true
    }, {
      id: 'price-3',
      eyebrow: 'GET YOUR DISCOUNT',
      title: 'Save 20%',
      description: 'Automatically applied when you keep 5+ items from your Fix.',
      bordered: true
    }]
  },
  stylistMatch: {
    eyebrow: 'get matched to a stylist',
    title: 'How your Stylist gets it right',
    backgroundDesktopCloudinaryAsset: assets.stylistMatchBackgrounds.desktop.cloudinaryAsset,
    backgroundMobileCloudinaryAsset: assets.stylistMatchBackgrounds.mobile.cloudinaryAsset,
    backgroundAlt: 'Woman standing outside in a sunny yard',
    foregroundCloudinaryAsset: assets.stylistMatchForeground.cloudinaryAsset,
    foregroundAlt: 'Woman wearing a light top and jeans while talking on a phone',
    outfitImages: assets.stylistMatchOutfits.map((image, index) => ({
      id: `stylist-match-outfit-${index + 1}`,
      ...image,
      alt: 'Outfit recommendation'
    })),
    cards: [{
      id: 'match-1',
      title: 'Learns your style + fit',
      description: 'A quick quiz tells your Stylist exactly what works for your body, taste and lifestyle.',
      icon: 'style'
    }, {
      id: 'match-2',
      title: 'Works with your lifestyle',
      description: 'Your Stylist shops for you—from work outfits and weekend basics to date-night pieces.',
      icon: 'home'
    }, {
      id: 'match-3',
      title: 'Stays within your budget',
      description: 'Tell us what you want to spend per clothing category. Your Stylist will stay within it.',
      icon: 'budget'
    }]
  },
  inYourFix: {
    eyebrow: 'IN your fix',
    title: '“What do I get?”',
    description: 'A quick quiz tells your Stylist exactly what works',
    cards: [{
      id: 'rosa',
      title: "Rosa's Fix",
      budget: '$30 - $180/item',
      stylist: 'Alero',
      description: 'The ask: Versatile work-to-dinner pieces. Her Stylist chose versatile pieces to fit her request.',
      avatarCloudinaryAsset: assets.inYourFixImages.rosa.avatar.cloudinaryAsset,
      avatarAlt: 'Stylist avatar for Alero',
      primaryCloudinaryAsset: assets.inYourFixImages.rosa.primary.cloudinaryAsset,
      primaryAlt: 'Rosa wearing a beige sleeveless top and jeans',
      secondaryCloudinaryAsset: assets.inYourFixImages.rosa.secondary.cloudinaryAsset,
      secondaryAlt: 'Rosa Fix outfit items'
    }, {
      id: 'jaimmy',
      title: "Jaimmy's Fix",
      budget: '$35 - $70/item',
      stylist: 'Hailley',
      description: 'Jaimmy wanted fresh looks for summer. Her Stylist chose pieces that work for weddings and brunches.',
      avatarCloudinaryAsset: assets.inYourFixImages.jaimmy.avatar.cloudinaryAsset,
      avatarAlt: 'Stylist avatar for Hailley',
      primaryCloudinaryAsset: assets.inYourFixImages.jaimmy.primary.cloudinaryAsset,
      primaryAlt: 'Jaimmy wearing a yellow dress outside',
      secondaryCloudinaryAsset: assets.inYourFixImages.jaimmy.secondary.cloudinaryAsset,
      secondaryAlt: 'Jaimmy Fix outfit items'
    }, {
      id: 'cam',
      title: "Cam's Fix",
      budget: '$50 - $120/item',
      stylist: 'Travis',
      description: 'Cam needed to update his shirts. His Stylist selected elevated pieces that still feel comfortable.',
      avatarCloudinaryAsset: assets.inYourFixImages.cam.avatar.cloudinaryAsset,
      avatarAlt: 'Stylist avatar for Travis',
      primaryCloudinaryAsset: assets.inYourFixImages.cam.primary.cloudinaryAsset,
      primaryAlt: 'Cam wearing a navy polo and shorts',
      secondaryCloudinaryAsset: assets.inYourFixImages.cam.secondary.cloudinaryAsset,
      secondaryAlt: 'Cam Fix outfit items'
    }, {
      id: 'kinley',
      title: 'Kinley’s Fix',
      budget: '$22 - $40/item',
      stylist: 'Gabrielle',
      description: 'Kinley’s mom requested school clothes that go beyond leggings. Her Stylist sent a variety of styles and colors.',
      avatarCloudinaryAsset: assets.inYourFixImages.kinley.avatar.cloudinaryAsset,
      avatarAlt: 'Stylist avatar for Gabrielle',
      primaryCloudinaryAsset: assets.inYourFixImages.kinley.primary.cloudinaryAsset,
      primaryAlt: 'Kinley wearing a white top and green backpack',
      secondaryCloudinaryAsset: assets.inYourFixImages.kinley.secondary.cloudinaryAsset,
      secondaryAlt: 'Kinley Fix outfit items'
    }]
  },
  control: {
    eyebrow: 'Transparent & flexible',
    title: 'You’re in control',
    description: '',
    cards: [{
      id: 'control-1',
      title: 'No subscription required',
      description: 'Get a Fix whenever you want—monthly, quarterly or just once.'
    }, {
      id: 'control-2',
      title: 'Budget by category',
      description: 'Set separate budgets for tops, bottoms, shoes and more.'
    }, {
      id: 'control-3',
      title: 'Free shipping and returns',
      description: 'We’ll include a pre-paid label so you can drop off at a convenient location.'
    }]
  },
  finalCta: {
    eyebrow: 'ALWAYS RISK FREE',
    title: 'Free styling + $30 off',
    description: 'Try before you buy with your first Fix. Feel your best confident self with our personal styling.',
    ctaLabel: 'Claim $30 off + free styling',
    ctaHref: '/signup'
  },
  faqs: {
    alignment: 'center',
    moduleTitle: {
      titleOrder: 'major-first',
      major: emptyRichText,
      title: titleRichText('FAQs'),
      subtitle: emptyRichText,
      titleFullWidth: false
    },
    items: [{
      defaultIsExpanded: true,
      heading: 'Are there actual human Stylists?',
      body: richText([paragraph('Yes, they’re real! We’re proud to work with passionate personal Stylists from all over the U.S. They’re fashion experts who are motivated to learn about your style and evolving needs to find the perfect pieces for you.')])
    }, {
      heading: 'Styling sounds expensive—what are pricing and fees like?',
      body: richText([paragraph('It’s your style at your price. Just tell us the range you feel comfortable spending and we’ll personalize everything to your budget. Set a price range by type of items (tops, pants, dresses, etc.) and we’ll stick to it (unless you give us permission for the occasional splurge). We carry a variety of price points: women’s and men’s items start at around $28, and run all the way up to about $500. And our kids’ items start at only $10.'), paragraph('You’ll pay a $20 styling fee per Fix order, which is then credited to whatever you keep. Plus, special discounts are available with every Fix!'), paragraph('Shipping, returns and exchanges are always free!')])
    }, {
      heading: 'Is this a subscription?',
      body: richText([paragraph('There is no subscription required. Your Stitch Fix experience is totally up to you. '), paragraph(''), paragraph('You can choose automatic deliveries as an easy way to get fresh style to your door on a regular schedule. Opt in to receive your Fixes at a frequency that’s right for you—every 2-3 weeks, every month, every other month or every three months.'), paragraph(''), paragraph('Prefer to get your Fixes on demand? No problem, just order whenever you’re looking for a wardrobe refresh.')])
    }, {
      heading: 'How do you nail my fit?',
      body: richText([paragraph('First, your style quiz gives us an understanding of your sizes and how you like your clothes to fit. We carry tons of brands in a wide range of sizes so our fashion Stylists can send you the best-fitting pieces. With each Fix, we’re able to further perfect your fit, knowing the items that have (and haven’t) worked for you. The more Fixes you get, the more precisely “you” they become. We always factor in the notes you send to your Stylist, too.')])
    }, {
      heading: 'How does my Stylist know what to send me?',
      body: richText([paragraph('Your online Stylist is your partner in style. They’re here to listen and help you achieve your best personal style. The more you share with us, the more your online Stylist will know what you love. Your style quiz answers help us get an idea of pieces you’ll like and match you with a Stylist. With each order, they get to know your style even better via the items that have (and haven’t) worked for you.'), paragraph('You can also leave a note for your Stylist whenever you schedule a style box shipment. Looking to update your workwear wardrobe? Have an event or vacation coming up? Big life change? Let us know!'), paragraph('Once you’ve gotten your first Fix, you’ll also have access to two more great Fix options. First, try a Themed Fix! From workwear to date night themes and more, just choose your Themed Fix, and your Stylist will personalize it to you. Or, choose a piece from your personally curated “Freestyle” shop, and have your Stylist create a Fix to complement it—an easy way to figure out what to wear with that item you fell in love with.\u00a0'), paragraph('In addition to the notes, some other tools we use to help send you pieces personalized just for you are: keeping your style profile up to date, giving feedback on the items in each Fix and your Fix history (what you keep and what you return).')])
    }, {
      heading: 'How do I communicate with my Stylist?',
      body: richText([paragraph('You can leave a note for your online personal Stylist whenever you schedule your next Fix—you can edit this note as much as you want up to the time your Stylist gets started. View, edit or add to your note directly on your Fixes page or in the Stitch Fix app after scheduling. Have a specific question? You can reach out directly to a Stylist via our Stylist Connect feature, exclusively in the Stitch Fix app. This is a great way to get personalized answers to style questions big or small from a real (human!) Stylist.')])
    }, {
      heading: 'What is StyleFile?',
      body: richText([paragraph('We’re glad you asked! StyleFile is an all-new, personalized experience that reveals your unique style personality.'), paragraph('StyleFile is a helpful tool that makes understanding your personal style easy. Start by exploring your unique combination of 5 style types, then get inspired by the details of your style personality.'), paragraph('Of course, your StyleFile helps your Stitch Fix Stylist curate a Fix that’s incredibly personal to you.\u00a0 Another pro? It makes shopping on your own a little less stressful—and a lot more fun.\u00a0'), paragraph('Don’t forget to share the fun—see what your friends and family got, and compare StyleFile notes.'), paragraph('Want to learn more about the StyleFile experience? Check out our blog!')])
    }, {
      heading: 'What range of sizes do you carry?',
      body: richText([paragraph('We carry a wide range of sizes for women, men and kids:'), paragraph('', [textBreakNode('Women: XS-3X (including petite, plus + maternity) '), textBreakNode('Men: XS-3XL (including big + tall) '), textNode('Kids: 2T-18')])])
    }, {
      heading: "Do you have kids' clothing?",
      body: richText([paragraph('We sure do! We carry kids’ clothes in sizes 2T - 18—starting at just $12.')])
    }, {
      heading: 'Do you offer petites and plus sizes?',
      body: richText([paragraph('Yes. We carry petite clothes styles designed with narrower shoulders, shorter sleeves and just-right hemlines in sizes from XS and 0, as well as petite plus sizes.'), paragraph(''), paragraph('We also have an extensive selection of perfect-fitting plus-size styles designed for curves (like jeans you’ll love and trending looks) in sizes up to 24W and 3X.')])
    }, {
      heading: 'How about maternity clothing?',
      body: richText([paragraph('Yes. We offer personalized style for this exciting time for moms to be. Your Stylist might even be one of the first to know you’re expecting. From cute, versatile styles that grow with you to easy-to-wear pieces that’ll help you feel confident in your new routine, we’ve got you covered for every stage.')])
    }, {
      heading: 'What brands do you carry?',
      body: richText([paragraph('', [textNode('We carry 2,000+ brands and styles for women, men and kids. You’ll find familiar faves like Madewell, Vuori, Birkenstock, The North Face, Free People, Adidas, Beyond Yoga, Travis Mayhew and many more, in addition to a variety of Stitch Fix exclusive brands. For a full list, click '), linkNode({
        href: 'https://www.stitchfix.com/our-brands',
        text: 'here'
      }), textNode('.')])])
    }, {
      heading: 'Can I browse and purchase pieces on my own?',
      body: richText([paragraph('Yes, we call it “Freestyle.” Once you’ve received your first Fix, you’ll find the Freestyle tab on your profile—you can browse your personalized Freestyle shop, curated with pieces just for you.'), paragraph(' With Freestyle, you can browse suggestions for items that complete your Fix looks, discover what’s trending for you based on your style preferences and so much more. Obsessed with a certain piece you wear again and again? Or did your Stylist send you the perfect pair of jeans? You can also shop for new colors, prints or sizes of pieces you’ve already purchased and have to have more of.')])
    }, {
      heading: 'How often will I receive a Fix?',
      body: richText([paragraph('It’s totally up to you! You can choose automatic deliveries as an easy way for fresh style to come on a regular schedule. Opt in to receive your Fixes at a frequency that’s right for you—every 2-3 weeks, every month, every other month or every three months.'), paragraph(''), paragraph('Prefer to get your Fixes on demand? No problem, just order a clothing box whenever you’re looking for a wardrobe refresh.')])
    }, {
      heading: 'How can I shop for my whole family?',
      body: richText([paragraph('With Stitch Fix’s personal styling service, it’s so easy to get personalized style for everyone, all in one place. We have lots of amazing clothes for women, men and kids—let our Stylists do the shopping for the whole family!'), paragraph('It’s easy to shop for the whole family in one place with a Family Account—just add an adult and/or up to 4 kids profiles to your account to simplify personalized style for everyone. When you do, the first styling fee is on us (plus discounts on your first family account Fix order). Make your own picks, or let us do the shopping and get time back.'), paragraph('', [textNode('Learn more about family accounts '), linkNode({
        href: 'https://support.stitchfix.com/hc/en-us/articles/360001688487-Can-I-manage-my-family-s-accounts-in-one-place-',
        text: 'here'
      }), textNode('.')])])
    }, {
      heading: 'How do I use Buy Now Pay Later options?',
      body: richText([paragraph("For eligible Fix and Freestyle purchases, we offer flexible Buy Now Pay Later options through our partnership with Stripe. These options are not available for StylePass and styling fee charges. Choose from Affirm, Klarna, or Afterpay to split your payment into installments. Simply select your preferred provider at checkout. You'll be redirected to complete the application process before finalizing your purchase. Please find more details below."), paragraph('', [breakTextNode(), boldTextNode('Affirm'), breakTextNode(' '), textNode("Affirm offers payment plans for purchases between $50 and $30,000. You can choose from 3, 6, 12, 18 or 36-month payment plans with rates from 0-36% APR based on creditworthiness. Affirm doesn't charge late fees, but late payments may affect your credit. For full details, please review Affirm's "), linkNode({
        href: 'https://www.affirm.com/terms',
        text: 'terms and conditions'
      }), textNode(' and '), linkNode({
        href: 'https://www.affirm.com/privacy',
        text: 'privacy policy.'
      }), textNode(' Affirm is subject to eligibility. See lending terms on '), linkNode({
        href: 'http://affirm.com/disclosures',
        text: 'affirm.com/disclosures'
      }), textNode('.')]), paragraph('', [boldTextNode('Klarna'), breakTextNode(' '), textNode('Klarna provides a "Pay in 4" option, allowing you to split your purchase into 4 interest-free payments over 6 weeks. This option is available for orders up to $1,000 and has no impact on your credit score when you apply. Late fees may apply if payments are missed. You also have the option to apply for monthly financing for purchases up to $10,000 and manage payments directly in the Klarna app. Monthly financing products are offered by WebBank, member FDIC. CA resident loans made or arranged pursuant to a California Financing Law license. For full details, please review Klarna\'s '), linkNode({
        href: 'https://www.klarna.com/us/legal/',
        text: 'terms and conditions'
      }), textNode(' and '), linkNode({
        href: 'https://www.klarna.com/us/privacy-and-security/',
        text: 'privacy policy'
      }), textNode('.\u00a0')]), paragraph('', [boldTextNode('Afterpay'), breakTextNode(' '), textNode('Afterpay is a service that allows us to offer you the ability to make purchases now and pay for them over time. If using the pay-in-4 option, you pay for your purchase total in four interest-free installments over six weeks. You can get additional time to pay for high value orders when you use the monthly payments option, an installment-based lending product. Please note, all items in your shopping bag must be eligible, and the order value must be between $10 and $1,000.\u00a0 Late fees may apply. Eligibility criteria apply.\u00a0 Loans to California residents made or arranged pursuant to a California Finance Lenders Law license. See '), linkNode({
        href: 'http://www.afterpay.com',
        text: 'www.afterpay.com'
      }), textNode(' for more details.')])])
    }]
  }
};